import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle, ShieldCheck, Loader2 } from 'lucide-react';
import apiClient from "../api/client";
import { PageShell } from '../components/layout/PageShell';


// Map backend errors to friendly user messages
const mapErrorToMessage = (error) => {
  const detail = error?.response?.data?.detail?.toLowerCase() || '';
  
  if (detail.includes('expired')) {
    return 'This reset link has expired. Please request a new one.';
  }
  if (detail.includes('invalid') || detail.includes('not found')) {
    return 'This reset link is invalid. Please request a new one.';
  }
  if (detail.includes('already used') || detail.includes('used')) {
    return 'This reset link has already been used. Please request a new one.';
  }
  if (detail.includes('rate') || detail.includes('too many')) {
    return 'Too many attempts. Please try again in a few minutes.';
  }
  if (detail.includes('password')) {
    return error.response?.data?.detail || 'Password does not meet requirements.';
  }
  
  return 'Something went wrong. Please try again or request a new reset link.';
};

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Token validation states
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');

  // Form states
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Password strength validation
  const validatePassword = (password) => {
    const issues = [];
    if (password.length < 8) issues.push('At least 8 characters');
    if (!/[a-zA-Z]/.test(password)) issues.push('At least one letter');
    if (!/[0-9]/.test(password)) issues.push('At least one number');
    return issues;
  };

  const passwordIssues = validatePassword(newPassword);
  const isPasswordValid = newPassword.length > 0 && passwordIssues.length === 0;
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = isPasswordValid && passwordsMatch && !isSubmitting && tokenValid;

  // Pre-check token validity on mount
  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setTokenError('No reset token provided. Please request a new password reset link.');
        setIsCheckingToken(false);
        return;
      }

      try {
        const res = await apiClient.get(`/auth/reset-token-check/${encodeURIComponent(token)}`);
        if (res.data?.valid) {
          setTokenValid(true);
        } else {
          // Map reason to friendly message
          const reason = res.data?.reason?.toLowerCase() || '';
          if (reason.includes('expired')) {
            setTokenError('This reset link has expired. Please request a new one.');
          } else if (reason.includes('used')) {
            setTokenError('This reset link has already been used. Please request a new one.');
          } else {
            setTokenError('This reset link is invalid. Please request a new one.');
          }
        }
      } catch (err) {
        // If endpoint fails, allow form submission (fallback to submit-time validation)
        console.warn('[ResetPassword] Token check failed, allowing form:', err);
        setTokenValid(true);
      } finally {
        setIsCheckingToken(false);
      }
    };

    checkToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!canSubmit) return;

    setIsSubmitting(true);

    try {
      await apiClient.post(`/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setIsSuccess(true);
    } catch (err) {
      setError(mapErrorToMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state (checking token)
  if (isCheckingToken) {
    return (
      <PageShell className="flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="surface-card rounded-2xl shadow-xl p-8 text-center">
            <Loader2 className="w-8 h-8 text-rose-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-300">Verifying reset link...</p>
          </div>
        </div>
      </PageShell>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <PageShell className="flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="surface-card rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-3">
              Password Reset Successful!
            </h1>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Link
              to="/auth"
              className="block w-full py-3 px-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition text-center"
            >
              Sign In
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  // Invalid/expired token state
  if (tokenError) {
    return (
      <PageShell className="flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="surface-card rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-3">
              Reset Link Issue
            </h1>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              {tokenError}
            </p>
            <Link
              to="/forgot-password"
              className="block w-full py-3 px-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition text-center"
            >
              Request New Reset Link
            </Link>
            <Link
              to="/auth"
              className="block w-full py-3 px-4 mt-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-xl font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition text-center"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  // Form state
  return (
    <PageShell className="flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="surface-card rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-rose-100 dark:bg-rose-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-rose-600 dark:text-rose-400" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-2">
              Reset Your Password
            </h1>
            <p className="text-slate-600 dark:text-slate-300">
              Enter your new password below.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                {error.includes('request a new') && (
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-red-600 dark:text-red-400 underline hover:text-red-800 dark:hover:text-red-200 mt-1 inline-block"
                  >
                    Request new reset link →
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="w-full rounded-xl surface-input py-3 pl-4 pr-12 placeholder-slate-400 dark:placeholder-slate-500 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                  required
                  autoFocus
                  data-testid="reset-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {/* Password Strength */}
              {newPassword.length > 0 && (
                <div className="mt-2 space-y-1">
                  {['At least 8 characters', 'At least one letter', 'At least one number'].map((rule) => {
                    const passed = !passwordIssues.includes(rule);
                    return (
                      <div key={rule} className="flex items-center gap-2 text-xs">
                        {passed ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border border-slate-300 dark:border-slate-600" />
                        )}
                        <span className={passed ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}>
                          {rule}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className={`w-full rounded-xl surface-input py-3 pl-4 pr-12 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-1 ${
                    confirmPassword.length > 0 && !passwordsMatch
                      ? 'border-red-300 dark:border-red-700 focus:border-red-500 focus:ring-red-500'
                      : 'focus:border-rose-500 focus:ring-rose-500'
                  }`}
                  required
                  data-testid="reset-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Passwords do not match
                </p>
              )}
              {confirmPassword.length > 0 && passwordsMatch && (
                <p className="mt-1 text-sm text-emerald-500 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  Passwords match
                </p>
              )}
            </div>

            {/* Security Notice */}
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 dark:text-amber-300">
                After resetting, you&apos;ll be signed out of all devices and need to sign in again.
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!canSubmit}
              className={`w-full py-3 px-4 rounded-xl font-semibold transition ${
                canSubmit
                  ? 'bg-rose-600 text-white hover:bg-rose-700'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed'
              }`}
              data-testid="reset-submit-btn"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Resetting...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </PageShell>
  );
}
